/**
 * PHSIMS Web Admin - Change Password Page
 * Author: Iswe Solutions Team
 * 
 * Page for users who must reset their password after first login (invited users).
 * Requires authentication. Matches PHSIMS login page design.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  LinearProgress,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import { apiClient } from '../services/api';
import { AppFooter } from './common/AppFooter';

const ChangePasswordPage: React.FC = () => {
  const navigate = useNavigate();
  
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  // Check if this is a required password reset
  const mustReset = localStorage.getItem('must_reset_password') === 'true';

  // Calculate password strength
  useEffect(() => {
    let strength = 0;
    if (newPassword.length >= 12) strength += 25;
    if (/[A-Z]/.test(newPassword)) strength += 25;
    if (/[a-z]/.test(newPassword)) strength += 25;
    if (/[0-9]/.test(newPassword)) strength += 12.5;
    if (/[^A-Za-z0-9]/.test(newPassword)) strength += 12.5;
    setPasswordStrength(strength);
  }, [newPassword]);

  const getStrengthColor = () => {
    if (passwordStrength < 50) return '#d63031';
    if (passwordStrength < 75) return '#fd7e14';
    if (passwordStrength < 100) return '#fdcb6e';
    return '#00b894';
  };

  const getStrengthLabel = () => {
    if (passwordStrength < 50) return 'Weak';
    if (passwordStrength < 75) return 'Fair';
    if (passwordStrength < 100) return 'Good';
    return 'Strong';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (passwordStrength < 100) {
      setError('Password does not meet all requirements');
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      await apiClient.post('/auth/force-reset-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      
      // Clear the reset flag
      localStorage.removeItem('must_reset_password');
      setSuccess(true);
    } catch (err: unknown) {
      setLoading(false);
      // apiClient throws plain Error with extracted message from backend response
      const message = err instanceof Error ? err.message : '';
      if (message && !message.startsWith('API Error:')) {
        setError(message);
      } else if (message) {
        setError('Failed to change password. Please check your password meets all requirements and try again.');
      } else {
        setError('Failed to change password. Please try again.');
      }
    }
  };

  // Success state
  if (success) {
    return (
      <Box
        sx={{
          height: '100vh',
          width: '100vw',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          background: '#0d3322',
          position: 'fixed',
          top: 0,
          left: 0,
        }}
      >
        <Box
          sx={{
            background: 'white',
            padding: { xs: '24px 20px', sm: '32px 40px' },
            borderRadius: { xs: '16px', sm: '20px' },
            boxShadow: '0 25px 80px rgba(0,0,0,0.5)',
            width: { xs: '100%', sm: 460 },
            maxWidth: { xs: '94%', sm: '94%' },
            borderTop: '5px solid #ef7d00',
            textAlign: 'center',
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 64, color: '#00b894', mb: 2 }} />
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: '#0d5c2e' }}>
            Password Changed!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Your password has been updated successfully.
          </Typography>
          <Button
            variant="contained"
            fullWidth
            onClick={() => navigate('/')}
            sx={{
              py: 1.5,
              backgroundColor: '#198a4b',
              '&:hover': { backgroundColor: '#146c43' },
              borderRadius: '10px',
              fontWeight: 600,
            }}
          >
            Continue to Dashboard
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        background: '#0d3322',
        position: 'fixed',
        top: 0,
        left: 0,
      }}
    >
      <Box
        sx={{
          background: 'white',
          padding: { xs: '24px 20px', sm: '32px 40px' },
          borderRadius: { xs: '16px', sm: '20px' },
          boxShadow: '0 25px 80px rgba(0,0,0,0.5)',
          width: { xs: '100%', sm: 460 },
          maxWidth: { xs: '94%', sm: '94%' },
          borderTop: '5px solid #ef7d00',
        }}
      >
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 2.5 } }}>
          <Box
            component="img"
            src="/znphi_logo.jpg"
            alt="ZNPHI Logo"
            sx={{
              width: { xs: 60, sm: 70 },
              height: 'auto',
              borderRadius: '8px',
              mb: 1,
              objectFit: 'contain',
            }}
          />
          <Typography
            sx={{
              color: '#0d5c2e',
              fontSize: { xs: '14px', sm: '16px' },
              fontWeight: 800,
              lineHeight: 1.3,
              mb: 0.5,
            }}
          >
            Public Health Security &<br />Information Management System
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontSize: { xs: '20px', sm: '24px' },
              fontWeight: 800,
              letterSpacing: '2px',
              color: '#ef7d00',
            }}
          >
            PHSIMS
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {mustReset ? 'Set Your Password' : 'Change Password'}
          </Typography>
        </Box>

        {/* Warning for forced reset */}
        {mustReset && (
          <Alert 
            severity="warning" 
            icon={<WarningIcon />} 
            sx={{ 
              mb: 2,
              borderRadius: '10px',
              fontSize: '13px',
            }}
          >
            You must set a new password before continuing.
          </Alert>
        )}

        {/* Error Message */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2,
              borderRadius: '10px',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              fontSize: '13px',
              py: 0.5,
            }}
          >
            {error}
          </Alert>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <TextField
              fullWidth
              type={showCurrentPassword ? 'text' : 'password'}
              placeholder={mustReset ? 'Temporary Password' : 'Current Password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={loading}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ color: '#198a4b', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowCurrentPassword(!showCurrentPassword)} edge="end" size="small">
                      {showCurrentPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  backgroundColor: '#f8fafc',
                  fontWeight: 500,
                  '& fieldset': { borderWidth: '2px', borderColor: '#e2e8f0' },
                  '&:hover fieldset': { borderColor: '#198a4b' },
                  '&.Mui-focused fieldset': { borderColor: '#198a4b' },
                  '&.Mui-focused': { backgroundColor: 'white', boxShadow: '0 0 0 3px rgba(25, 138, 75, 0.1)' },
                },
                '& .MuiOutlinedInput-input': { padding: '12px 12px 12px 0', fontSize: '14px' },
              }}
            />

            <TextField
              fullWidth
              type={showNewPassword ? 'text' : 'password'}
              placeholder="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={loading}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ color: '#198a4b', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end" size="small">
                      {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  backgroundColor: '#f8fafc',
                  fontWeight: 500,
                  '& fieldset': { borderWidth: '2px', borderColor: '#e2e8f0' },
                  '&:hover fieldset': { borderColor: '#198a4b' },
                  '&.Mui-focused fieldset': { borderColor: '#198a4b' },
                  '&.Mui-focused': { backgroundColor: 'white', boxShadow: '0 0 0 3px rgba(25, 138, 75, 0.1)' },
                },
                '& .MuiOutlinedInput-input': { padding: '12px 12px 12px 0', fontSize: '14px' },
              }}
            />
            
            {/* Password strength indicator */}
            {newPassword && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Password strength
                  </Typography>
                  <Typography variant="caption" sx={{ color: getStrengthColor(), fontWeight: 600 }}>
                    {getStrengthLabel()}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={passwordStrength}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': { backgroundColor: getStrengthColor(), borderRadius: 3 },
                  }}
                />
              </Box>
            )}

            <TextField
              fullWidth
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
              size="small"
              error={confirmPassword !== '' && newPassword !== confirmPassword}
              helperText={confirmPassword !== '' && newPassword !== confirmPassword ? 'Passwords do not match' : ''}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ color: '#198a4b', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" size="small">
                      {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  backgroundColor: '#f8fafc',
                  fontWeight: 500,
                  '& fieldset': { borderWidth: '2px', borderColor: '#e2e8f0' },
                  '&:hover fieldset': { borderColor: '#198a4b' },
                  '&.Mui-focused fieldset': { borderColor: '#198a4b' },
                  '&.Mui-focused': { backgroundColor: 'white', boxShadow: '0 0 0 3px rgba(25, 138, 75, 0.1)' },
                },
                '& .MuiOutlinedInput-input': { padding: '12px 12px 12px 0', fontSize: '14px' },
              }}
            />

            {/* Password requirements */}
            <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
              <Typography variant="caption" fontWeight="bold" display="block" gutterBottom sx={{ color: '#0d5c2e' }}>
                Password Requirements:
              </Typography>
              <Typography variant="caption" component="ul" sx={{ pl: 2, m: 0, lineHeight: 1.6 }}>
                <li style={{ color: newPassword.length >= 12 ? '#00b894' : '#666' }}>At least 12 characters</li>
                <li style={{ color: /[A-Z]/.test(newPassword) ? '#00b894' : '#666' }}>One uppercase letter</li>
                <li style={{ color: /[a-z]/.test(newPassword) ? '#00b894' : '#666' }}>One lowercase letter</li>
                <li style={{ color: /[0-9]/.test(newPassword) ? '#00b894' : '#666' }}>One number</li>
                <li style={{ color: /[^A-Za-z0-9]/.test(newPassword) ? '#00b894' : '#666' }}>One special character</li>
              </Typography>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || passwordStrength < 100 || newPassword !== confirmPassword || !currentPassword}
              sx={{
                py: 1.5,
                mt: 0.5,
                backgroundColor: '#198a4b',
                '&:hover': { backgroundColor: '#146c43' },
                '&:disabled': { backgroundColor: '#ccc' },
                borderRadius: '10px',
                fontWeight: 600,
                fontSize: '15px',
              }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Change Password'}
            </Button>
          </Box>
        </form>

        {/* Footer */}
        <AppFooter variant="auth" />
      </Box>
    </Box>
  );
};

export default ChangePasswordPage;
